import React, { useEffect, useState, useMemo } from 'react';
import { ref, query, orderByChild, get } from 'firebase/database';
import { db } from '../lib/firebase';
import { User } from '../types/user';
import { Link } from 'react-router-dom';
import { MapPin, Building, Filter, X } from 'lucide-react';
import { SearchBar } from '../components/search/SearchBar';
import { FilterPanel } from '../components/search/FilterPanel';
import { ViewControls } from '../components/search/ViewControls';
import { SearchFilters, defaultFilters } from '../types/search';
import { logger } from '../lib/logger';

const AllProfiles = () => {
  const [profiles, setProfiles] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<SearchFilters>(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersRef = ref(db, 'vcards');
        const usersQuery = query(usersRef, orderByChild('createdAt'));
        const snapshot = await get(usersQuery);
        
        if (snapshot.exists()) {
          const usersData = snapshot.val();
          const publicProfiles = Object.entries(usersData)
            .map(([id, data]) => ({
              id,
              ...(data as Omit<User, 'id'>)
            }))
            .filter(user => user.isPublic)
            .reverse();
          
          setProfiles(publicProfiles);
        }
      } catch (error) {
        const errorMessage = logger.error('database', 'Erreur lors de la récupération des profils', error);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const filteredProfiles = useMemo(() => {
    return profiles.filter(profile => {
      const searchTerm = filters.searchTerm.toLowerCase();
      if (searchTerm) {
        const searchableFields = [
          profile.prenom,
          profile.nom,
          profile.entreprise,
          profile.poste
        ].map(field => field?.toLowerCase() || '');
        
        if (!searchableFields.some(field => field.includes(searchTerm))) {
          return false;
        }
      }

      if (filters.location.pays && !profile.pays?.toLowerCase().includes(filters.location.pays.toLowerCase())) {
        return false;
      }
      if (filters.location.ville && !profile.ville?.toLowerCase().includes(filters.location.ville.toLowerCase())) {
        return false;
      }

      if (filters.metier && !profile.poste?.toLowerCase().includes(filters.metier.toLowerCase())) {
        return false;
      }

      if (filters.entreprise && !profile.entreprise?.toLowerCase().includes(filters.entreprise.toLowerCase())) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      switch (filters.sortBy) {
        case 'alpha':
          return `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`);
        case 'views':
          return (b.views || 0) - (a.views || 0);
        case 'date':
        default:
          return 0;
      }
    });
  }, [profiles, filters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse flex justify-center items-center">
              <div className="h-8 w-48 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderProfileCard = (profile: User) => (
    <Link
      key={profile.id}
      to={`/p/${profile.username}`}
      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1 overflow-hidden"
    >
      <div 
        className="h-20 sm:h-24 bg-gradient-to-r"
        style={{
          background: profile.qrStyle?.fgColor 
            ? `linear-gradient(135deg, ${profile.qrStyle.fgColor}dd 0%, ${profile.qrStyle.fgColor}99 100%)`
            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
        }}
      />
      <div className="relative px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="flex justify-center">
          <div className="-mt-10 sm:-mt-12 w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-white overflow-hidden bg-gray-100 shadow-lg">
            {profile.logoUrl ? (
              <img
                src={profile.logoUrl}
                alt={`${profile.prenom} ${profile.nom}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                <span className="text-xl sm:text-2xl font-bold">
                  {profile.prenom?.[0]}{profile.nom?.[0]}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 sm:mt-4">
          <div className="text-center">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-1">
              {profile.prenom} {profile.nom}
            </h3>
            {profile.poste && (
              <p className="text-sm text-gray-600 line-clamp-1 mt-0.5">{profile.poste}</p>
            )}
          </div>
          <div className="mt-3 space-y-1.5">
            {profile.entreprise && (
              <div className="flex items-center text-sm text-gray-500">
                <Building className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="line-clamp-1">{profile.entreprise}</span>
              </div>
            )}
            {profile.ville && (
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="line-clamp-1">{profile.ville}, {profile.pays}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  const renderProfileList = (profile: User) => (
    <Link
      key={profile.id}
      to={`/p/${profile.username}`}
      className="block bg-white p-3 sm:p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-center space-x-3 sm:space-x-4">
        <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden bg-gray-100">
          {profile.logoUrl ? (
            <img
              src={profile.logoUrl}
              alt={`${profile.prenom} ${profile.nom}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
              <span className="text-lg sm:text-xl font-bold">
                {profile.prenom?.[0]}{profile.nom?.[0]}
              </span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
            {profile.prenom} {profile.nom}
          </h3>
          {profile.poste && (
            <p className="text-sm text-gray-600 truncate">{profile.poste}</p>
          )}
          <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500">
            {profile.entreprise && (
              <div className="flex items-center">
                <Building className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">{profile.entreprise}</span>
              </div>
            )}
            {profile.ville && (
              <div className="flex items-center mt-1 sm:mt-0">
                <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">{profile.ville}, {profile.pays}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <SearchBar
            value={filters.searchTerm}
            onChange={(searchTerm) => setFilters({ ...filters, searchTerm })}
          />
        </div>

        <div className="lg:flex lg:gap-8">
          {/* Bouton filtres mobile */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-center px-4 py-2 bg-white rounded-lg shadow text-gray-700 hover:bg-gray-50"
            >
              {showFilters ? (
                <>
                  <X className="h-5 w-5 mr-2" />
                  Masquer les filtres
                </>
              ) : (
                <>
                  <Filter className="h-5 w-5 mr-2" />
                  Afficher les filtres
                </>
              )}
            </button>
          </div>

          {/* Panneau de filtres */}
          <div className={`
            ${showFilters ? 'block' : 'hidden'} lg:block
            lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0
          `}>
            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
            />
          </div>

          {/* Liste des profils */}
          <div className="flex-1">
            <div className="mb-6">
              <ViewControls
                filters={filters}
                onFiltersChange={setFilters}
              />
            </div>

            <div className={filters.viewMode === 'grid' 
              ? "grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3"
              : "space-y-3 sm:space-y-4"
            }>
              {filteredProfiles.map(profile => 
                filters.viewMode === 'grid' 
                  ? renderProfileCard(profile)
                  : renderProfileList(profile)
              )}
            </div>

            {filteredProfiles.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun profil ne correspond à vos critères de recherche.</p>
              </div>
            )}
            {error && (
              <div className="text-center py-12">
                <p className="text-red-500">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProfiles;
